import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Band,
  BandCount,
  BandHead,
  BadgeRow,
  ColumnHead,
  Elapsed,
  Empty,
  EmptyNote,
  EmptyTitle,
  MetaDispo,
  MetaFixed,
  MetaFlex,
  MetaSep,
  Muted,
  RowMeta,
  StackedRow,
  StackedTop,
  StatePill,
  TicketId,
  TicketRow,
} from '../styled-comps';
import { PARKED_LABEL, QUEUE_STATE, STATE_LABEL } from '../constants';
import { elapsedSince, fmtElapsed, shortId, stateLabel, stateTone, TONE } from '../utils';

const columns = ({ showRegion, showOwner }) =>
  [
    '86px',
    '58px',
    showRegion ? '70px' : null,
    showOwner ? 'minmax(80px, 1fr)' : null,
    'minmax(90px, 0.8fr)',
    'minmax(150px, 1.6fr)',
    'minmax(196px, 1.5fr)',
  ]
    .filter(Boolean)
    .join(' ');

export const RowHeader = ({ showRegion, showOwner }) => (
  <ColumnHead $cols={columns({ showRegion, showOwner })}>
    <div>Ticket</div>
    <div>Src</div>
    {showRegion && <div>Region</div>}
    {showOwner && <div>Owner</div>}
    <div>Queue</div>
    <div>Disposition</div>
    <div>State · badges</div>
  </ColumnHead>
);

export const EmptyPane = ({ title, note }) => (
  <Empty>
    <EmptyTitle>{title}</EmptyTitle>
    <EmptyNote>{note}</EmptyNote>
  </Empty>
);

/**
 * Falls back to the pre-split boolean because two backend branches are in
 * flight and only one of them carries `parked_on`: whichever lands first,
 * the chip survives, degrading to the side-less word rather than vanishing
 * from a pane that is roughly half parked. Reads the looked-up label rather
 * than the raw value so an unmapped side draws no pill instead of a blank
 * one.
 */
const parkedLabel = row =>
  PARKED_LABEL[row.parked_on] || (row.is_parked ? 'PARKED' : null);

/**
 * SEEN is drawn only beside a reply-owed row, where it is the sharpest
 * thing on the board: an agent read the customer and walked away. Anywhere
 * else the newest message is our own, so the chip only ever said the agent
 * had read their own reply — true on 24 of 29 quiet rows, and worth
 * nothing on any of them.
 */
const Badges = ({ row }) => (
  <>
    {row.state === QUEUE_STATE.REPLY_OWED && row.is_seen && (
      <StatePill $tone={TONE.info}>SEEN</StatePill>
    )}
    {row.never_replied && <StatePill $tone={TONE.error}>NEVER REPLIED</StatePill>}
    {parkedLabel(row) && <StatePill $tone={TONE.well}>{parkedLabel(row)}</StatePill>}
  </>
);

// The board's whole workflow is "copy the id, paste it into the agent
// inbox", and only 8 of the 36 characters are on screen — so a ✓ over a
// failed write would leave the supervisor with nothing and no idea why.
// null = idle, true = copied, false = the write failed.
const useCopyId = id => {
  const [copyState, setCopyState] = useState(null);
  const resetTimer = useRef(null);

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const onCopy = useCallback(() => {
    const settle = ok => {
      setCopyState(ok);
      clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCopyState(null), 1600);
    };
    // Rejects in practice: document not focused, permission denied, or a
    // testenv served over plain http, where `clipboard` is absent entirely.
    if (!navigator.clipboard?.writeText) {
      settle(false);
      return;
    }
    navigator.clipboard.writeText(id).then(
      () => settle(true),
      () => settle(false),
    );
  }, [id]);

  return { copyState, onCopy };
};

const CopyableId = ({ id, copyState, onCopy }) => (
  <TicketId
    type="button"
    onClick={onCopy}
    title={
      copyState === false ? 'Could not copy — select it manually' : 'Copy full ticket ID'
    }
  >
    <span>{shortId(id)}</span>
    <span>{copyState === null ? '⧉' : copyState ? '✓' : '✕'}</span>
  </TicketId>
);

// Both fields are nullable on every row and absent entirely until the
// backend ships them, so they degrade the way `owner_name` already does.
const DASH = '—';

/**
 * Unrouted and unclassified are stated rather than left blank. The
 * classifier sets both on its first hit, so 316 of 375 live bot-owned
 * tickets already carry them: a dash marks the brief window before that,
 * which is a real state and not the common case.
 */
const MetaLine = ({ row, showRegion, showOwner }) => {
  const parts = [
    <MetaFixed key="src">{row.source}</MetaFixed>,
    showRegion && <MetaFixed key="region">{row.region || 'Unidentified'}</MetaFixed>,
    showOwner && (
      <MetaFlex key="owner" title={row.owner_name || undefined}>
        {row.owner_name || DASH}
      </MetaFlex>
    ),
    <MetaFlex key="team" title={row.team_name || undefined}>
      {row.team_name || DASH}
    </MetaFlex>,
  ].filter(Boolean);

  return (
    <RowMeta>
      {parts.map((part, i) => (
        <React.Fragment key={part.key}>
          {i > 0 && <MetaSep>·</MetaSep>}
          {part}
        </React.Fragment>
      ))}
    </RowMeta>
  );
};

/**
 * The disposition gets its own line rather than a fifth slot on the meta
 * line. Paths run to 45 characters against a pane 400px wide, and beside an
 * owner and a team name like `teacher_policies_cs` the row truncated all
 * three at once — `Sharan Prabhak… · teacher_policies_… · compliance.fra…`,
 * which loses the field this was added to show.
 */
const DispoLine = ({ row }) => (
  <RowMeta>
    <MetaDispo title={row.disposition || undefined}>{row.disposition || DASH}</MetaDispo>
  </RowMeta>
);

/**
 * A row is never clickable — the workflow is copy the id and paste it into the
 * agent inbox, which already searches by UUID. Only the id itself is a button.
 */
const Ticket = ({ row, nowSeconds, showRegion, showOwner, cols }) => {
  const { copyState, onCopy } = useCopyId(row.id);
  const elapsed = elapsedSince(row.state_since, nowSeconds);
  const tone = stateTone(row.state, elapsed);
  const label = stateLabel(row.state);
  const hot = row.state === QUEUE_STATE.REPLY_OWED && tone === TONE.errorHeavy;

  return (
    <TicketRow $cols={cols} $hot={hot}>
      <CopyableId id={row.id} copyState={copyState} onCopy={onCopy} />
      <Muted>{row.source}</Muted>
      {showRegion && <Muted>{row.region || 'Unidentified'}</Muted>}
      {showOwner && <Muted>{row.owner_name || DASH}</Muted>}
      <Muted>{row.team_name || DASH}</Muted>
      <Muted title={row.disposition || undefined}>{row.disposition || DASH}</Muted>
      <BadgeRow>
        {label && (
          <StatePill $tone={tone}>
            {label} {elapsed !== null && fmtElapsed(elapsed)}
          </StatePill>
        )}
        <Badges row={row} />
      </BadgeRow>
    </TicketRow>
  );
};

// Carries no state pill: the band heading above it already names the state,
// so the row spends its top line on the clock and the badges instead.
const BandedTicket = ({ row, nowSeconds, showRegion, showOwner }) => {
  const { copyState, onCopy } = useCopyId(row.id);
  const elapsed = elapsedSince(row.state_since, nowSeconds);
  const tone = stateTone(row.state, elapsed);
  const hot = row.state === QUEUE_STATE.REPLY_OWED && tone === TONE.errorHeavy;

  return (
    <StackedRow $tone={tone} $hot={hot}>
      <StackedTop>
        <CopyableId id={row.id} copyState={copyState} onCopy={onCopy} />
        {elapsed !== null && <Elapsed $tone={tone}>{fmtElapsed(elapsed)}</Elapsed>}
        <Badges row={row} />
      </StackedTop>
      <MetaLine row={row} showRegion={showRegion} showOwner={showOwner} />
      <DispoLine row={row} />
    </StackedRow>
  );
};

const MemoTicket = React.memo(Ticket);
const MemoBandedTicket = React.memo(BandedTicket);

const BAND_ORDER = [
  QUEUE_STATE.REPLY_OWED,
  QUEUE_STATE.CUSTOMER_QUIET,
  QUEUE_STATE.NO_INBOUND,
];

/**
 * Partitions without re-sorting. The server already orders every pane
 * most-at-risk-first, and that order is what makes the 50-row cap safe —
 * re-sorting here would quietly disagree with the rows the cap chose to
 * send. Rows in no known band (a resolved row carries no state) fall into a
 * trailing headless group rather than vanishing.
 */
const toBands = rows => {
  const byState = new Map(BAND_ORDER.map(state => [state, []]));
  const unbanded = [];

  rows.forEach(row => {
    const band = byState.get(row.state);
    if (band) band.push(row);
    else unbanded.push(row);
  });

  const bands = BAND_ORDER.filter(state => byState.get(state).length).map(state => ({
    state,
    rows: byState.get(state),
  }));

  return unbanded.length ? [...bands, { state: null, rows: unbanded }] : bands;
};

/**
 * `capped` suppresses the per-band counts. The server ships at most
 * `QUEUE_PANE_ROW_CAP` rows, reply-owed first, so on a capped pane a band
 * count is the size of the slice and not of the state — and it renders in a
 * pill identical to the pane's own total, where it would read as one.
 */
const TicketRows = ({ rows, nowSeconds, showRegion, showOwner, banded, capped }) => {
  if (!banded) {
    const cols = columns({ showRegion, showOwner });
    return (
      <>
        {rows.map(row => (
          <MemoTicket
            key={row.id}
            row={row}
            nowSeconds={nowSeconds}
            showRegion={showRegion}
            showOwner={showOwner}
            cols={cols}
          />
        ))}
      </>
    );
  }

  return (
    <>
      {toBands(rows).map(band => (
        <Band key={band.state || 'other'}>
          {band.state && (
            <BandHead $tone={stateTone(band.state, 0)}>
              <span>{STATE_LABEL[band.state]}</span>
              {!capped && <BandCount>{band.rows.length}</BandCount>}
            </BandHead>
          )}
          {band.rows.map(row => (
            <MemoBandedTicket
              key={row.id}
              row={row}
              nowSeconds={nowSeconds}
              showRegion={showRegion}
              showOwner={showOwner}
            />
          ))}
        </Band>
      ))}
    </>
  );
};

export default TicketRows;
